import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, ApiError, onPermissionsChanged, primePermissionStamp } from "@/lib/api";
import { browserZone } from "@/lib/time";
import { useRefreshOnFocus } from "@/lib/use-refresh-on-focus";

/**
 * Permission names, mirroring the server's constants class.
 *
 * Kept as a const object rather than bare strings so a typo is a TypeScript error. A
 * mistyped permission in a `can()` call would silently render nothing — which looks like
 * a missing feature rather than a bug, and is therefore easy to ship.
 */
export const Permission = {
  TicketRead: "ticket.read",
  TicketCreate: "ticket.create",
  TicketUpdate: "ticket.update",
  TicketAssign: "ticket.assign",
  TicketResolve: "ticket.resolve",
  TicketReopen: "ticket.reopen",
  TicketComment: "ticket.comment",
  TicketCommentInternal: "ticket.comment.internal",

  /**
   * Correcting who a request is for.
   *
   * ⚠️ Rendering only, and the scope caveat bites here as it does on `sla.manage`: permissions
   * reach the browser WITHOUT their scope, so `can("ticket.requester.change")` is true for
   * somebody who may correct requesters on one team's queue and no other. The ticket's own
   * `canChangeRequester` flag is what the detail screen actually draws from, and the endpoint
   * re-checks the caller against the specific record — which is where Team and Category scope
   * apply.
   */
  TicketRequesterChange: "ticket.requester.change",

  /**
   * Passing a request to another team (D-96).
   *
   * ⚠️ Rendering only, and permissions reach the browser WITHOUT their scope — so this is true
   * for an agent holding it at Team scope, which is the ordinary case. Whether they may route
   * a PARTICULAR request is decided per record on the server, which answers 404 when they may
   * not.
   */
  TicketRoute: "ticket.route",

  TeamManage: "team.manage",
  RoleManage: "role.manage",
  UserManage: "user.manage",
  CategoryManage: "category.manage",

  /**
   * Setting a team's or a category's response targets.
   *
   * ⚠️ Rendering only, and the caveat matters more here than usual: permissions reach the
   * browser WITHOUT their scope, so `can("sla.manage")` is true for a team administrator who
   * may edit exactly one team's targets. It decides whether the tab is drawn, never which
   * team it may be used on — the server checks the scope against the team on every write.
   */
  SlaManage: "sla.manage",

  ReportRead: "report.read",

  /**
   * The two reporting tiers (D-67).
   *
   * ⚠️ Rendering only, as every permission here is — the server re-checks all three on every
   * request under `/api/reports`, and the rows are additionally pinned by the tier and bounded
   * by the query filter and RLS. Nothing in the browser decides who sees what.
   *
   * ⚠️ Holding one of these does NOT make somebody an administrator. They are catalogued
   * `Capability` rather than `Administrative` precisely so a team lead reading their own
   * queue's figures does not acquire the badge, the backlog navigation of D-39 or a demotion
   * entry. `isAdministrator` on the session stays the single definition (D-36).
   */
  ReportSelf: "report.self",
  ReportTeam: "report.team",

  ReportExport: "report.export",
  AuditRead: "audit.read",
  UserImpersonate: "user.impersonate",
  AdminBreakGlass: "admin.breakglass",
  LegalHoldManage: "legalhold.manage",
  SettingsRead: "settings.read",
  SettingsManage: "settings.manage",
  IntegrationManage: "integration.manage",
  NotificationTemplateManage: "notification.template.manage",
} as const;

export type PermissionName = (typeof Permission)[keyof typeof Permission];

export interface SessionUser {
  userId: string;
  displayName: string;
  email: string | null;
  isActive: boolean;
  permissions: readonly PermissionName[];

  /** The caller's OWN roles and teams, by name. Never anyone else's. */
  roles: readonly string[];
  teams: readonly string[];

  /**
   * Sensitivity levels this caller is cleared to read, from the teams they belong to.
   *
   * For wording an empty state, nothing else. It says nothing about any ticket — not that
   * one exists, not how many — and both enforcement layers filter regardless. Someone with
   * an empty set sees no request but their own, which looked exactly like a working queue
   * with nothing in it until this was sent.
   */
  clearedLevels: readonly string[];

  /**
   * The timezone every date on screen is rendered in — this user's override, or their
   * site's, or the tenant default.
   *
   * ⚠️ Presentation only. SLA deadlines are computed in the TICKET'S site timezone, so
   * changing this cannot move a commitment.
   */
  timeZoneId: string;

  /** True when they have set their own rather than inheriting their site's. */
  timeZoneIsOverridden: boolean;

  /**
   * Enabled feature flags, for rendering only — exactly like `permissions`.
   *
   * The server asks its own feature gate to build this, so a locked-off flag reports off
   * here exactly as it does at the endpoint behind it. Every flagged endpoint re-checks
   * and returns 404 when off, so editing this list in devtools reveals nothing.
   */
  features: readonly string[];

  /**
   * Whether this caller administers the system, decided by the SERVER.
   *
   * ⚠️ Not computed here from `permissions`, and that restraint is the point. D-36 says
   * `AdministrativeGrants` is the single definition of "administrator" and nothing may
   * compute it a second time — a `can("team.manage") || can("settings.read")` here would be
   * exactly that second time, and it would drift from the roster badge and the demotion
   * endpoint the first time somebody catalogues a new administrative permission.
   *
   * Rendering only, like every other field on this object: it decides whether unbuilt
   * product surfaces appear in the navigation. Those routes are reachable by URL either way
   * and each renders `NotBuiltYet`, so this hides a signpost, not a door.
   */
  isAdministrator: boolean;

  /**
   * Whether this caller may raise a request on somebody else's behalf (D-51), decided by the
   * SERVER.
   *
   * ⚠️ Not derivable from `permissions` and that is why it is sent. Permissions arrive here
   * as bare names with no scope, so `can("ticket.create")` is true for every employee in the
   * organisation — the baseline Requester role holds it at Own scope. Gating the picker on
   * that would offer it to all ~29 sites and have the server refuse every submission, which
   * reads as a broken screen rather than as a permission somebody lacks.
   *
   * ⚠️ NOT interchangeable with `isAdministrator`, which is a different set: the desk agents
   * this exists for hold no administrative grant at all.
   *
   * Rendering only. The create endpoint re-checks the same expression and returns 403.
   */
  canRaiseForOthers: boolean;

  /**
   * Whether this person may name an assignee while raising a request (D-105).
   *
   * ⚠️ From the SERVER, computed from the same any-of the create endpoint applies —
   * `ticket.create` at Team scope AND `ticket.assign`. Never `can("ticket.assign")` here:
   * permissions reach the browser without scope, so that would draw the field for people the
   * endpoint refuses. A different set again from `canRaiseForOthers` and `isAdministrator`.
   *
   * Rendering only. The endpoint re-checks and answers 403.
   */
  canAssignAtCreation: boolean;

  lastSignInAtUtc: string | null;

  /**
   * An opaque value that changes whenever anything deciding this user's access changes.
   *
   * ⚠️ Carries no meaning and grants nothing — it is compared for equality and never read.
   * It exists so a browser can notice its own permissions moved without polling: the same
   * value rides out on every authenticated response, and the api helper re-reads this
   * profile when it changes.
   *
   * Deliberately not a v7 GUID, server-side: a v7 value embeds the moment it was created,
   * which would publish when each person's access last changed to anybody watching.
   */
  permissionStamp: string;

  /**
   * The running build, e.g. `1.0.0+3e29727`. Shown at the foot of the sidebar.
   *
   * Comes from the server rather than a value baked into this bundle, so it describes the
   * API actually serving the request. A version compiled into the SPA would keep reporting
   * itself from a cached bundle after a deploy — which is precisely the moment someone
   * looks at it.
   */
  version: string;

  /** How this person has asked to be contacted. Their own record and nobody else's. */
  contact: SessionContact;

  /**
   * The site this person is placed at, by directory sync or an administrator.
   *
   * ⚠️ Decides whether the new-request form ASKS where they are. Somebody already placed
   * should not be picking their own facility out of twenty-nine — the answer is known, and
   * offering the list invites a different one.
   *
   * ⚠️ Rendering only. The server derives a ticket's site from the requester's record
   * whatever the form posts, so clearing this to reveal the picker reveals a control the
   * server ignores. Null is ordinary — a cloud-only account whose OU matched no site — and
   * is exactly when the question does need asking.
   */
  siteId: string | null;
  siteName: string | null;

  /** Who this person reports to, and whether that manager may see their requests. */
  manager: SessionManager;

  /** Which relationship tabs their ticket list should offer. */
  ticketTabs: SessionTicketTabs;
}

/**
 * Whether this person has anything to put behind the two relationship tabs (D-47).
 *
 * ⚠️ Two facts about the caller's own relationships, and nothing about any ticket — not
 * which, not how many, not in what category. They exist so most of ~2,600 employees are not
 * shown two permanently empty tabs, and so an empty "My team's requests" is never read as
 * "my reports have no problems" when it means "nobody reports to you".
 *
 * ⚠️ Rendering only. Both tabs re-derive their rows from the session server-side, so forcing
 * one on gets an empty page rather than somebody else's.
 */
export interface SessionTicketTabs {
  hasSharedWithMe: boolean;
  hasTeamRequests: boolean;
}

/**
 * The caller's own reporting line, and their answer about it (D-46).
 *
 * ⚠️ The one access grant in this system given by the person the records are about. It is
 * shown on their profile, changed by them alone, and there is no request an administrator
 * can send that turns it on for somebody else.
 *
 * ⚠️ Rendering only, like everything else on the session. `PUT /api/me/manager-access`
 * re-checks the flag and reads the user id from the session, so a browser that edits any of
 * this gains nothing.
 */
export interface SessionManager {
  /** Null when the directory has not recorded one — ordinary, and its own empty state. */
  managerUserId: string | null;
  managerDisplayName: string | null;
  managerHasAvatar: boolean;

  /** Their own answer. False for everybody until they give it. */
  sharesRequests: boolean;

  /**
   * Whether the mechanism is switched on at all (`reports.manager-access`).
   *
   * Separate from having a manager so the profile can tell "nobody is recorded as your
   * manager" apart from "an administrator turned this off" — two different sentences with
   * two different remedies, and a toggle that silently does nothing is worse than either.
   */
  available: boolean;
}

/**
 * The signed-in person's own contact details.
 *
 * ⚠️ Rides on `/api/me` rather than having its own endpoint so the new-request form can
 * pre-fill the callback number without a second round trip — and, more to the point,
 * without a "get somebody's phone number" endpoint existing at all. One that took a user id
 * would be a staff directory, which is not something a requester needs.
 */
export interface SessionContact {
  /** E.164. Rendered through `formatPhone`, never shown raw. */
  contactPhone: string | null;
  contactPhoneExtension: string | null;

  /**
   * The mobile text messages would go to. A different field from `contactPhone`, because a
   * contact number is often a desk line with an extension and consenting to be texted on a
   * line that cannot receive a text is a consent record that means nothing.
   */
  smsMobileNumber: string | null;

  /**
   * Whether they have agreed to be texted about their own requests.
   *
   * The server's answer over both stored fields rather than a stored flag — consent
   * recorded against no number is not consent, and the browser must not have to know that.
   */
  smsConsent: boolean;
}

/**
 * Flag keys the UI renders against. Mirrors the server's FeatureFlags constants — the
 * same reason permissions are constants: a magic string at a call site is a typo away
 * from silently never matching, which for a feature check means silently never showing.
 */
export const Feature = {
  TicketAttachments: "ticket.attachments",
  AiAssistant: "ai.assistant",
  EmailNotifications: "notify.email",
  TodoFollowUps: "todo.follow-ups",

  /**
   * PDF form of the reports figures (`BL-029`).
   *
   * ⚠️ Locked off server-side until a typeface has been licensed for embedding, so this reads
   * false everywhere today and the button is simply absent. Rendering only, as ever — the
   * endpoint 404s when the flag is off regardless of what this bundle believes.
   */
  ReportPdfExport: "report.pdf",
} as const;

export type FeatureName = (typeof Feature)[keyof typeof Feature];

interface SessionContextValue {
  user: SessionUser | null;
  isLoading: boolean;
  error: string | null;
  /**
   * Whether the UI should offer this capability.
   *
   * ⚠️ This is a RENDERING decision, never an authorization one. The server re-checks
   * every request against the database, so a user who edits their permission list in
   * devtools gains buttons that return 403 and nothing else. Treating this as security
   * is precisely the mistake the mock made (defect D1).
   */
  can: (permission: PermissionName) => boolean;

  /**
   * Whether a feature is switched on. Same caveat as `can` — rendering only.
   *
   * A feature being on says nothing about whether this user may use it; that is still a
   * permission question the server answers.
   */
  hasFeature: (feature: FeatureName) => boolean;

  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * @param quiet Re-read without showing the splash.
   *
   * ⚠️ A live permission change must not blank the application. `RequireSession` renders a
   * "Signing you in…" splash whenever `isLoading` is true, so refreshing the loud way would
   * throw the whole UI away and rebuild it every time an administrator saved anything — for
   * a role edit, that is every holder of the role at once. The user sees a screen whose
   * controls simply change.
   */
  const load = useCallback(async (quiet = false) => {
    if (!quiet) setIsLoading(true);
    setError(null);

    try {
      const me = await api<SessionUser>("/api/me");
      setUser(me);

      // So the next differing stamp reads as a change rather than a first sighting, and
      // this response does not immediately trigger another refresh of itself.
      primePermissionStamp(me.permissionStamp ?? null);
    } catch (e) {
      // A 401 has already navigated to sign-in inside the api helper, so there is
      // nothing useful to show and no reason to retry here.
      if (e instanceof ApiError && e.status === 401) return;

      setUser(null);
      setError(e instanceof ApiError ? e.message : "Could not load your profile.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // The server said this user's access changed, on some response we were making anyway.
  // Quiet, so the application does not blank while it re-reads.
  useEffect(() => onPermissionsChanged(() => void load(true)), [load]);

  // ⚠️ The backstop, and it covers a real gap rather than duplicating the above. The stamp
  // only arrives on a response, so a tab sitting idle — nobody clicking, nothing polling —
  // makes no requests and hears nothing. Coming back to the tab is the cheapest possible
  // signal that somebody is about to look at this screen again, which is exactly when a
  // stale permission set matters. Also quiet.
  useRefreshOnFocus(() => void load(true));

  const value = useMemo<SessionContextValue>(
    () => ({
      user,
      isLoading,
      error,
      // Deny while loading and when unauthenticated. The alternative — assuming
      // permission until told otherwise — flashes admin controls to every user during
      // the first paint.
      can: (permission) => user?.permissions.includes(permission) ?? false,

      // Off while loading, for the same reason: a feature panel that appears and then
      // vanishes on first paint reads as a bug, and for the AI assistant it would read
      // as one that was switched off in a hurry.
      hasFeature: (feature) => user?.features.includes(feature) ?? false,

      refresh: load,
    }),
    [user, isLoading, error, load],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error("useSession must be used within a SessionProvider");
  return context;
}

/**
 * Renders nothing but a neutral splash until the session is known.
 *
 * ⚠️ This exists because of a real defect. The app shell and the routed page used to
 * render immediately while `/api/me` was still in flight, so an unauthenticated visitor
 * saw the internal dashboard — navigation, queues, admin entries and the ticket lists
 * screens still draw from mock data — for the second or two before the 401 arrived and
 * the redirect fired. It looked exactly like a successful sign-in followed by an error.
 *
 * Nothing was actually disclosed: every API call was refused and the visible rows were
 * fabricated. But "the data was fake" is not a defence anyone should have to make, and
 * a person watching over a shoulder in a clinical corridor cannot tell the difference.
 * The structure alone — which teams exist, which admin surfaces exist — is information
 * this app has no reason to show a stranger.
 *
 * The splash deliberately shows no product detail: no navigation, no counts, no names.
 */
export function RequireSession({ children }: { children: React.ReactNode }) {
  const { user, isLoading, error } = useSession();

  if (isLoading) return <SessionSplash message="Signing you in…" />;

  // No user and no error means the 401 handler in the api helper has already started
  // navigating to Entra. Say so plainly rather than flashing the app on the way out.
  if (!user) {
    return <SessionSplash message={error ?? "Taking you to sign in…"} isError={error !== null} />;
  }

  // Authenticated but deactivated. An administrator has switched this account off, and
  // the honest answer is a sentence — not an app full of controls that all return 403.
  if (!user.isActive) {
    return (
      <SessionSplash
        message="Your account is not active. Contact the service desk if you think this is wrong."
        isError
      />
    );
  }

  return <>{children}</>;
}

function SessionSplash({ message, isError = false }: { message: string; isError?: boolean }) {
  return (
    <div
      className="flex min-h-screen items-center justify-center bg-background px-6"
      role="status"
      aria-live="polite"
    >
      <div className="text-center">
        <p className="text-sm font-medium text-foreground">ARS Service Desk</p>
        <p
          className={
            isError ? "mt-2 text-sm text-destructive" : "mt-2 text-sm text-muted-foreground"
          }
        >
          {message}
        </p>
      </div>
    </div>
  );
}

/** Convenience for the common single-permission check. */
export function useCan(permission: PermissionName): boolean {
  return useSession().can(permission);
}

/**
 * True when the signed-in user works tickets rather than only raising them.
 *
 * Derived from a permission, never from a stored role or mode. The mock kept this in
 * localStorage as `tf-mode`, which meant any user could grant themselves the agent
 * console from the browser console.
 */
export function useIsAgent(): boolean {
  return useCan(Permission.TicketCommentInternal);
}

/**
 * True when the signed-in user administers the system.
 *
 * ⚠️ Reads the server's answer rather than deriving one. See `SessionUser.isAdministrator`:
 * a second definition of "administrator" is the thing D-36 exists to prevent, and the
 * browser holding a permission list makes writing one here very easy and very wrong.
 *
 * False while the session loads, matching `can` — the alternative flashes administrative
 * navigation to every user on first paint.
 */
export function useIsAdministrator(): boolean {
  return useSession().user?.isAdministrator ?? false;
}

/**
 * The timezone this reader's dates are rendered in — the single answer for the whole screen.
 *
 * ⚠️ THE SERVER'S ANSWER, not the browser's, and that is the entire point. `timeZoneId` is
 * resolved by `TimeZones.ResolveAsync` as their own override, then their site's, then the
 * tenant default, and the same value cuts the day buckets on analytics, reports and the
 * follow-up list. Rendering an instant with a bare `toLocaleString()` used the browser's zone
 * instead, so those two disagreed on any laptop away from its site — and the personal
 * timezone control changed nothing but its own label.
 *
 * ⚠️ Falls back to the browser only where there is no session to read, which is a signed-out
 * screen. Anywhere else that fallback would be the defect returning, so it is not offered as
 * an argument and every formatter in `lib/time` requires a zone.
 *
 * ⚠️ Presentation only. SLA deadlines are computed server-side in the TICKET'S site zone
 * (D-72), so nothing rendered through this can move a commitment.
 */
export function useZone(): string {
  return useSession().user?.timeZoneId ?? browserZone();
}
